import { useCallback, useState } from 'react';
import { MaybeCognitoError } from '@move-frontend/auth';
import { IDetailsFormData } from '../types/IDetailsFormData';
import { signUp } from '../../../../../auth/sign-up/domain/use-cases/signUp';
import { updateUserAttributes } from '../../../../../auth/user/domain/use-cases/updateUserAttributes';
import { getStateAbbreviationForName } from '../../../../domain/united-states/domain/use-cases/getStateAbbreviationForName';
import { formatE164PhoneNumber } from '../helpers/formatE164PhoneNumber';

export type FinalSubmitHandler = (
	data: IDetailsFormData,
) => void | Promise<void>;

interface IDetailsSubmitOptions {
	finalSubmitHandler: FinalSubmitHandler;
}

export function useDetailsSubmit({
	finalSubmitHandler,
}: IDetailsSubmitOptions) {
	const [signUpError, setSignUpError] = useState<MaybeCognitoError | undefined>(
		undefined,
	);
	const [saveDetailsError, setSaveDetailsError] = useState<
		MaybeCognitoError | undefined
	>(undefined);

	const submit = useCallback(async (data: IDetailsFormData) => {
		try {
			await handleSignUp(data);
		} catch (error) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setSignUpError(error);
			return;
		}

		try {
			await handleSaveDetails(data);
		} catch (error) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			setSaveDetailsError(error);
			return;
		}

		await finalSubmitHandler(data);
	}, []);

	return { signUpError, saveDetailsError, submit };
}

async function handleSignUp(data: IDetailsFormData) {
	if (!hasSignUpData(data)) {
		return;
	}

	await signUp(data);
}

interface IDetailsFormSignUpData extends IDetailsFormData {
	shouldCreateNewUserAccount: true;
	password: string;
}

function hasSignUpData(data: IDetailsFormData): data is IDetailsFormSignUpData {
	return (
		data.shouldCreateNewUserAccount === true &&
		typeof data.password === 'string'
	);
}

async function handleSaveDetails(data: IDetailsFormData) {
	if (!data.saveDetails) {
		return;
	}

	await updateUserAttributes({
		family_name: data.lastName,
		given_name: data.firstName,
		phone_number: formatE164PhoneNumber(data.phoneNumber),
		address: {
			street_address: data.address,
			locality: data.city,
			postal_code: data.zipCode,
			region: getStateAbbreviationForName(data.state),
		},
		'custom:license_plate_number': data.licensePlateNumber,
	});
}
